import React from 'react';
import { AlertCircle, X, HelpCircle, CheckCircle2 } from 'lucide-react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'success' | 'info';
    loading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    type = 'danger',
    loading = false
}) => {
    if (!isOpen) return null;

    const getIcon = () => {
        switch (type) {
            case 'danger': return <AlertCircle size={32} className="text-red-500" />;
            case 'warning': return <HelpCircle size={32} className="text-amber-500" />;
            case 'success': return <CheckCircle2 size={32} className="text-emerald-500" />;
            default: return <HelpCircle size={32} className="text-brand-500" />;
        }
    };

    const getButtonClass = () => {
        switch (type) {
            case 'danger': return 'bg-red-600 hover:bg-red-700 shadow-red-200';
            case 'warning': return 'bg-amber-500 hover:bg-amber-600 shadow-amber-200';
            case 'success': return 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200';
            default: return 'bg-brand-600 hover:bg-brand-700 shadow-brand-200';
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-surface-900/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl relative overflow-hidden animate-scale-in">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-50 rounded-full transition-all"
                >
                    <X size={20} />
                </button>

                <div className="p-8 md:p-10 flex flex-col items-center text-center">
                    <div className={`w-20 h-20 rounded-[1.5rem] flex items-center justify-center mb-6 ${type === 'danger' ? 'bg-red-50' :
                            type === 'warning' ? 'bg-amber-50' :
                                type === 'success' ? 'bg-emerald-50' : 'bg-brand-50'
                        }`}>
                        {getIcon()}
                    </div>

                    <h3 className="text-xl font-black text-surface-900 mb-2 leading-tight">
                        {title}
                    </h3>

                    <p className="text-surface-500 font-medium text-sm leading-relaxed mb-8">
                        {message}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 w-full">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-surface-400 hover:text-surface-600 hover:bg-surface-50 transition-all border border-surface-100"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className={`flex-1 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${getButtonClass()}`}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
